<script setup>

    import moment from 'moment'

    import { useRouter } from 'vue-router'

    import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useAuthStore, useClientStore, useClientSingleStore, useNotificationStore } from '@/Stores'

    import { util } from '@/Helpers'

    import PrimaryButton from '@/Components/Forms/PrimaryButton.vue'

    import ClientDisposition from '@/Components/Forms/ClientDisposition.vue'
    import ClientDataCompletionStatus from '@/Components/Client/ClientDataCompletionStatus.vue'
    import { ExclamationTriangleIcon, CheckBadgeIcon } from '@heroicons/vue/16/solid'
    import { TrophyIcon } from '@heroicons/vue/16/solid'
    import ContextMenu from 'primevue/contextmenu'
    import { FilterMatchMode } from '@primevue/core/api';
    import Modal from '@/Components/App/Modal.vue'
    import InputLabel from '@/Components/Forms/InputLabel.vue'

    const authStore = useAuthStore()
    const { user } = storeToRefs(authStore)
    const clientStore = useClientStore()
    const { clientCompletionList, lastUpdated, dataCompletionLoading, dataCompletionUserContext } = storeToRefs(clientStore)
    const clientSingleStore = useClientSingleStore()
    const { dispositionsRequiringReasons } = storeToRefs(clientSingleStore)
    const notificationStore = useNotificationStore()

    const router = useRouter()

    const latteBaseUrl = import.meta.env.VITE_APP_URL
    const clientCoffeeBaseUrl = import.meta.env.VITE_COFFEE_BASEURL + '/staff/sales-manager/information/pid/'
    const clientCoffeeNotesUrl = import.meta.env.VITE_COFFEE_BASEURL + '/staff/sales-manager/view-notes/pid/'

    const loading = ref(true)

    const dispositionPendingUpdate = ref(false)
    const dispositionReasonsModalOptionList = ref([])
    const dispositionReasonsModalSelectedOptions = ref([])

    const dispositionReasonsModalVisible = computed(() => {
        return (dispositionPendingUpdate.value !== false && dispositionPendingUpdate.value !== '' && dispositionReasonsModalOptionList.value && dispositionReasonsModalOptionList.value.length > 0);
    })

    const contextMenuSelection = ref(null)
    const cm = ref()

    const menuModel = ref([
        {
            label: 'Open in New Tab',
            command: () => {
                const url = router.resolve({
                    name: 'client.profile',
                    params: { client: contextMenuSelection.value.id }
                }).href
                window.open(url, '_blank')
            }
        },
        {
            label: 'Copy Link',
            command: () => {
                const url = latteBaseUrl + router.resolve({
                    name: 'client.profile',
                    params: { client: contextMenuSelection.value.id }
                }).href
                navigator.clipboard.writeText(url)
            }
        },
        {
            separator: true
        },
        {
            label: 'Coffee Profile',
            command: () => {
                const url = clientCoffeeBaseUrl + contextMenuSelection.value.coffee_profile_id
                window.open(url, '_blank')
            }
        },
        {
            label: 'Coffee Notes',
            command: () => {
                const url = clientCoffeeNotesUrl + contextMenuSelection.value.coffee_profile_id
                window.open(url, '_blank')
            }
        }
    ])

    const completionProgress = computed(() => {
        return clientStore.getOverallDataCompletionProgress(tableData);
    })

    const completionLeaderboard = computed(() => {

        return clientStore.getDataCompletionLeaderBoard(tableData)

    })

    function transformClientData(clients) {
        return clients.map(client => ({
            progress: clientSingleStore.getProfileDataCompletionStatus(client.data_completion_status),
            id: client.id,
            coffee_profile_id: client.coffee_user_profile_id,
            uuid: client.uuid,
            clientType: client.type,
            disposition: client.disposition,
            disposition_updated_at: client.disposition_updated_at,
            name: `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Unnamed Client',
            name_complete: client.first_name && client.last_name && client.first_name.length > 0 && client.last_name.length > 0,
            address: client.address || { city: 'N/A', state: '' },
            address_validity: client.address_validity || null,
            data_completion_status: client.data_completion_status || {},
            created_at: client.created_at,
            aggregate_updated_by: client.aggregate_updated_by,
            aggregate_updated_at: client.aggregate_updated_at,
        }))
    }

    const tableData = ref([])

    const progressOptions = ref(['Good', 'Great', 'Poor' ])
    const typeOptions = ref([ 'Lead', 'Prospect', 'Client' ])

    const filters = ref({
        progress: { value: null, matchMode: FilterMatchMode.EQUALS },
        clientType: { value: null, matchMode: FilterMatchMode.EQUALS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    })

    function syncTableDataFromStore() {
        tableData.value = transformClientData(clientCompletionList.value)
    }

    async function loadClientData() {
        if (clientCompletionList.value && clientCompletionList.value.length > 0 && lastUpdated.value && moment().diff(moment(lastUpdated.value), 'seconds') < 30) {
            syncTableDataFromStore()
            loading.value = false
            return;
        }
    }

    onMounted(async () => {
        loadClientData()
        clientStore.resetDataCompletionRefreshTimer()
    })

    onUnmounted(() => {
        clientStore.stopDataCompletionRefreshTimer()
    })


    watch(() => dataCompletionUserContext.value, async (newUser, oldUser) => {
        const newUserId = newUser?.user_id ?? newUser?.id ?? undefined
        const oldUserId = oldUser?.user_id ?? oldUser?.id ?? undefined
        if (newUserId) {
            loading.value = true
           try {
                dataCompletionLoading.value = true
                const response = await clientStore.getClientForCompletionData(newUserId)
                await clientStore.hydrateClientCompletionData(response.data)
                syncTableDataFromStore()
            } catch (error) {
                console.error('Error loading client data:', error)
            } finally {
                clientStore.resetDataCompletionRefreshTimer()
                loading.value = false
                dataCompletionLoading.value = false
            }
        }
    }, { immediate: true })

    watch(clientCompletionList, () => {
        syncTableDataFromStore()
    }, { deep: true })

    function onRowSelect(event) {
        const clientId = event.data.id
        if (event?.originalEvent.metaKey || event?.originalEvent.ctrlKey) {
            const url = router.resolve({
                name: 'client.profile',
                params: { client: clientId }
            }).href
            window.open(url, '_blank')
            return;
        } else {
            router.push({ path: '/client/' + clientId })
        }
    }

    async function handleDispositionUpdate(clientUuid, updatedValue) {

        dispositionReasonsModalOptionList.value = clientSingleStore.getDispositionReasonOptionsList(updatedValue.disposition)

        console.log('Updating disposition...', updatedValue)

        if (dispositionsRequiringReasons.value.includes(updatedValue.disposition)) {
            dispositionPendingUpdate.value = updatedValue.disposition
            await new Promise(resolve => {
                const stopWatching = watch(dispositionPendingUpdate, (value) => {
                    if (value === false) {
                    stopWatching()
                    resolve()
                    }
                })
            })
        }

        await clientSingleStore.updateClientDisposition(clientUuid, {
            type: updatedValue.clientType,
            disposition: updatedValue.disposition,
            disposition_reasons: dispositionReasonsModalSelectedOptions.value
        }).then(async response => {
            clientStore.hydrateSingleClient(response.data)
            syncTableDataFromStore()
            notificationStore.add(dispositionReasonsModalSelectedOptions.value.length > 0 ? 'Updated Disposition & Reasons' : 'Updated Disposition')
        }).catch(error => {
            console.error('Error updating disposition:', error)
            notificationStore.add('Error Updating Disposition', 'error', 'Please try again')
        }).finally(() => {
            dispositionReasonsModalSelectedOptions.value = []
            dispositionPendingUpdate.value = false
            clientStore.resetDataCompletionRefreshTimer()
        })
    }

    const onRowContextMenu = (event) => {
        cm.value.show(event.originalEvent)
    }

</script>

<template>

    <div
        :class="[
            'p-3 md:p-5 grid grid-cols-1 md:grid-cols-12 gap-4',
            {
                'bg-zinc-200 pointer-events-none': loading
            }
        ]"
    >
        <div class="md:col-span-8">
            <h5 class="text-stone-500 font-semibold text-md md:text-xs tracking-wide pb-2 md:mb-0">
                Progress &amp; Quality Distribution
            </h5>
            <Skeleton v-if="loading" width="100%" height="20px" class="mb-2" />
            <MeterGroup
                v-else
                :value="completionProgress"
                :pt="{
                    root: 'gap-0.5',
                    meters: 'h-5 rounded-lg overflow-hidden',
                    labelList: 'gap-x-6 gap-y-2',
                    label: 'text-sm font-semibold mt-2 text-lifeworx-blue-700',
                    labelMarker: 'size-3'
                }"
            />
        </div>
        <div class="md:col-span-4">
            <h5 class="text-stone-500 font-semibold text-md md:text-xs tracking-wide pb-2 md:mb-0">
                Last Updated
            </h5>
            <Skeleton v-if="loading" width="100%" height="20px" class="mb-2" />
            <MeterGroup
                v-else
                :value="completionLeaderboard"
                :pt="{
                    root: 'gap-0.5',
                    meters: 'h-5 rounded-lg overflow-hidden',
                    labelList: 'gap-x-6 gap-y-2',
                    label: 'text-sm font-semibold mt-2 text-lifeworx-blue-700',
                    labelMarker: 'size-3'
                }"
            />
        </div>

    </div>

    <div>
    <DataTable
        class="min-w-[820px] md:min-w-0"
        v-model:filters="filters"
        :value="tableData"
        dataKey="id"
        filterDisplay="menu"
        :pt="{
            root: 'text-xs',
            tablecontainer: 'overflow-x-auto md:!overflow-visible',
            thead: loading ? 'sticky top-28 z-10 backdrop-blur backdrop-filter opacity-50 bg-white/75' : 'sticky top-28 z-10 backdrop-blur backdrop-filter bg-white/75',
            column: {
                filter: 'ms-0',
                title: 'text-zinc-200'
            }
        }"
        :loading="loading"
        contextMenu
        selectionMode="single"
        @rowSelect="onRowSelect"
        @row-contextmenu="onRowContextMenu"
        v-model:contextMenuSelection="contextMenuSelection"
        stripedRows
    >

        <template #empty>
            <div class="text-lg font-semibold text-zinc-500 text-center min-h-80 pt-20">
                {{ loading ? 'Loading...' : 'No Client, Prospect, or Lead profile data found. Switch to a new Client Manager or adjust your filter selections.' }}
            </div>
        </template>

        <Column
            field="progress"
            filterField="progress"
            :showFilterMatchModes="false"
            :pt="{
                root: 'w-10 p-0 pl-5 relative',
            }"
        >
            <template #body="{ data }">
                <div
                    :class="[
                        'ml-2 size-6 rounded-full justify-center items-center flex',
                        data.progress === 'great' ? 'bg-lifeworx-green-600/75 border-2 border-dashed border-lifeworx-yellow-500 outline outline-2 outline-offset-0 outline-lifeworx-green-600/75' : '',
                        data.progress === 'good' ? 'bg-lifeworx-green-600/75' : '',
                        data.progress === 'poor' ? 'bg-lifeworx-red-600/75' : '',
                    ]"
                >
                    <TrophyIcon
                        v-if="data.progress === 'great'"
                        class="text-lifeworx-yellow-400 size-4"
                    />
                </div>
            </template>

            <template #filter="{ filterModel, filterCallback }">
                <h4 class="pl-4 pt-4 text-lifeworx-blue-600 font-bold uppercase text-sm tracking-wide">
                    Show Only:
                </h4>
                <Listbox
                    v-model="filterModel.value"
                    :options="progressOptions"
                    style="min-width: 8rem"
                    :pt="{
                        root: 'border-none shadow-none',
                        option: 'pl-4 gap-2 bg-white hover:bg-zinc-100'
                    }"
                    checkmark
                >
                    <template #option="slotProps">
                        <div class="flex items-center gap-2">
                            <span>{{ slotProps.option }} Data</span>
                        </div>
                    </template>
                </Listbox>
            </template>
        </Column>

        <Column
            header="Disposition"
            field="clientType"
            :showFilterMatchModes="false"
            class="w-44 md:w-72"
            :pt="{ }"
        >
            <template #body="{ data }">
                <ClientDisposition
                    :modelValue="{ clientType: data.clientType, disposition: data.disposition }"
                    @update:modelValue="handleDispositionUpdate(data.uuid, $event)"
                    theme="button"
                    @click.prevent.stop
                    :title="data.disposition_updated_at ? `Disposition updated ${moment(data.disposition_updated_at).fromNow()}` : ''"
                />
            </template>

            <template #filter="{ filterModel, filterCallback }">
                <h4 class="pl-4 pt-4 text-lifeworx-blue-600 font-bold uppercase text-sm tracking-wide">
                    Show Only:
                </h4>
                <Listbox
                    v-model="filterModel.value"
                    :options="typeOptions"
                    style="min-width: 14rem"
                    :pt="{
                        root: 'border-none shadow-none',
                        option: 'pl-4 gap-2 bg-white hover:bg-zinc-100'
                    }"
                    checkmark
                >
                    <template #option="slotProps">
                        <div class="flex items-center gap-2">
                            <span>{{ slotProps.option }} Profiles</span>
                        </div>
                    </template>
                </Listbox>
            </template>


        </Column>

        <Column
            header="Name &amp; Location"
            field="name"
            :showFilterMatchModes="false"
            :showClearButton="true"
            class="min-w-[12rem] md:min-w-20"
        >
            <template #body="{ data }">
                <div class="flex flex-col gap-1">
                    <span class="font-semibold text-sm md:text-base">
                        {{ data.name }}
                        <template v-if="data.name_complete === false">
                            <ExclamationTriangleIcon class="inline-block size-4 text-lifeworx-orange-500/50 ml-1" />
                        </template>
                    </span>
                    <span class="text-xs font-semibold text-zinc-400">
                        <template v-if="data.address_validity === 'N' && data.address.city && data.address.state">
                            <ExclamationTriangleIcon class="inline-block size-4 text-lifeworx-yellow-500/75 mr-0.5" />
                        </template>
                        <template v-if="(data.address_validity === 'N' || !data.address_validity) && (!data.address.city || !data.address.state)">
                            <ExclamationTriangleIcon class="inline-block size-4 text-lifeworx-red-500/75 mr-0.5" />
                        </template>
                        <template v-else-if="data.address_validity === 'Y-1-MATCH'">
                            <CheckBadgeIcon class="inline-block size-4 text-lifeworx-green-500/75 mr-0.5" />
                        </template>
                        {{ data.address.city ? data.address.city + ', ' : '' }}{{ data.address.state }}
                    </span>
                </div>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text"  placeholder="Search by name" />
            </template>
        </Column>

        <Column
            header="Data Progress"
            class="min-w-20"
        >
            <template #body="{ data }">
                <div class="flex">
                    <ClientDataCompletionStatus
                        :subject="data"
                    />
                </div>
            </template>
        </Column>

        <Column
            header="Date Added &amp; Updated By"
            class="hidden xl:table-cell min-w-15"
        >
            <template #body="{ data }">
                <div class="flex flex-col gap-0.5">
                    <span
                        class="font-semibold"
                        :title="moment(data.created_at).format('MMMM Do YYYY, h:mm:ss a')"
                    >
                        Added {{ moment(data.created_at).fromNow() }}
                    </span>
                    <span
                        v-if="data.aggregate_updated_by"
                        class="flex"
                        :title="moment(data.aggregate_updated_at).format('MMMM Do YYYY, h:mm:ss a')"
                    >
                        Updated {{ moment(data.aggregate_updated_at).fromNow() }} by {{ data.aggregate_updated_by.first_name }} {{ data.aggregate_updated_by.last_name }}
                    </span>
                    <span v-else class="italic text-zinc-400 ml-2">———</span>
                </div>
            </template>
        </Column>

    </DataTable>
    </div>

    <ContextMenu
        ref="cm"
        :model="menuModel"
        @hide="contextMenuSelection = null"
    />

    <Modal
        :show="dispositionReasonsModalVisible"
    >
        <div class="p-2">
            <h2 class="text-xl font-bold text-lifeworx-blue-700">
                Additional Information Requested
            </h2>
            <div class="mt-6 mx-8 h-fit">
                <InputLabel
                    for="dashboard-modal-disposition-reasons"
                    :value="`Reason(s) for ${util.titleCase(util.underscoreToSpace(dispositionPendingUpdate))} Disposition (Select all that apply)`"
                    class="font-semibold text-stone-400 h-fit"
                >
                    <Listbox
                        id="dashboard-modal-disposition-reasons"
                        v-model="dispositionReasonsModalSelectedOptions"
                        :options="dispositionReasonsModalOptionList"
                        optionLabel="name"
                        optionValue="id"
                        :maxSelectedLabels="2"
                        class="w-full h-fit"
                        scrollHeight="24rem"
                        :pt="{
                            root: { class: 'text-xs shadow-none outline-none border border-opacity-50 p-4 sm:text-sm/6 h-fit' },
                            overlay: { class: 'text-sm !z-modal' },
                            header: { class: 'hidden'},
                            option: {
                                class: 'hover:bg-lifeworx-blue-600/30 selected:text-lifeworx-red-600 gap-4 px-12 aria-selected:bg-lifeworx-blue-600/10 aria-selected:text-lifeworx-blue-900 aria-[selected=false]:text-zinc-500/75',
                            },
                            optioncheckicon: 'size-4 text-lifeworx-blue-600',
                            optionblankicon: 'size-4',
                        }"
                        checkmark
                        multiple
                        striped
                    />
                </InputLabel>
            </div>

            <div class="mt-6 flex justify-end">

                <PrimaryButton
                    :class="[
                        'min-w-60 bg-lifeworx-blue-600',
                        {
                            'bg-lifeworx-blue-600 hover:bg-lifeworx-blue-700': dispositionReasonsModalSelectedOptions.length === 0,
                            'cursor-pointer bg-lifeworx-green-600 hover:bg-lifeworx-green-700': dispositionReasonsModalSelectedOptions.length > 0
                        }
                    ]"
                    @click="() => dispositionPendingUpdate = false"
                >
                    {{ dispositionReasonsModalSelectedOptions.length > 0 ? 'Save Reason Selection' + (dispositionReasonsModalSelectedOptions.length > 1 ? 's' : '') : 'Skip Requirement' }}
                </PrimaryButton>

            </div>
        </div>
    </Modal>

</template>
